import React, { ReactNode } from 'react';

import { ChipProps, Icon } from '@column/column-ui-kit';

import { type Action } from '~/lib/authz';
import { isACHBatchAllFailed, isACHBatchAllInvalid } from '~/lib/transfers/achBatch';
import { getTransferURL } from '~/lib/transfers/utils';
import { ACHTransferBatch, ACHTransferBatchRecord, BankAccountWithDetails } from '~/typings/API';

export type TransferStatusMeta = { type: ChipProps['type']; label: string; icon: ReactNode };

export const TRANSFER_STATUS_META: Record<string, TransferStatusMeta> = {
  pending: { type: 'default', label: 'Pending', icon: <Icon.CircleBlank /> },
  valid: { type: 'info', label: 'Valid', icon: <Icon.CircleCheck /> },
  invalid: { type: 'danger', label: 'Invalid', icon: <Icon.CircleCross /> },
  succeeded: { type: 'success', label: 'Created', icon: <Icon.CircleCheck /> },
  failed: { type: 'danger', label: 'Failed', icon: <Icon.CircleCross /> },
};

export type BatchStatusMeta = { type: ChipProps['type']; label: string; icon: ReactNode };

export const BATCH_STATUS_META: Record<string, BatchStatusMeta> = {
  pending: { type: 'default', label: 'Pending', icon: <Icon.CircleBlank /> },
  validating: { type: 'info', label: 'Validating', icon: <Icon.CirclePendingOne /> },
  validated: { type: 'info', label: 'Validated', icon: <Icon.CirclePendingTwo /> },
  processing: { type: 'info', label: 'Processing', icon: <Icon.CirclePendingThree /> },
  completed: { type: 'success', label: 'Completed', icon: <Icon.CircleCheck /> },
  failed: { type: 'danger', label: 'Failed', icon: <Icon.CircleCross /> },
  expired: { type: 'danger', label: 'Expired', icon: <Icon.CircleCross /> },
  canceled: { type: 'danger', label: 'Canceled', icon: <Icon.CircleCross /> },
};

const DEFAULT_BATCH_STATUS: BatchStatusMeta = {
  type: 'default',
  label: '',
  icon: <Icon.CircleBlank />,
};

export const transferUrlFor = (transfer: ACHTransferBatchRecord): string | undefined =>
  transfer.achTransferId
    ? getTransferURL(transfer.achTransferId, transfer.type === 'debit' ? 'ach_debit' : 'ach_credit')
    : undefined;

export const getBatchStatusDisplay = (batch: ACHTransferBatch): BatchStatusMeta => {
  if (isACHBatchAllInvalid(batch)) return { type: 'danger', label: 'All Invalid', icon: <Icon.CircleCross /> };
  if (isACHBatchAllFailed(batch)) return { type: 'danger', label: 'All Failed', icon: <Icon.CircleCross /> };
  return BATCH_STATUS_META[batch.status] ?? { ...DEFAULT_BATCH_STATUS, label: batch.status };
};

export const hasCreatableTransfers = (batch: ACHTransferBatch): boolean =>
  batch.status === 'validated' && batch.totalCount - batch.validationFailedCount > 0;

// Confirming a batch creates ACH credits and/or debits, so the user needs write permission for each
// direction the batch actually contains (scoped to the originating bank account). Returns a message
// naming the missing permission(s), or undefined when the user is authorized for everything present.
export const getCreateTransfersPermissionMessage = (
  batch: ACHTransferBatch,
  isAuthorized: (action: Action) => boolean,
  bankAccount?: BankAccountWithDetails
): string | undefined => {
  const missingPermissions: string[] = [];
  const bankAccountId = batch.importConfig?.nacha?.bankAccountId;
  const actionBankAccount = bankAccountId
    ? {
        id: bankAccountId,
        owners: bankAccount?.owners,
      }
    : undefined;

  if (
    batch.validatedTotalCreditAmount > 0 &&
    !isAuthorized({
      permission: 'achCredits',
      permissionLevel: 'write',
      bankAccount: actionBankAccount,
    })
  ) {
    missingPermissions.push('ACH credits');
  }

  if (
    batch.validatedTotalDebitAmount > 0 &&
    !isAuthorized({
      permission: 'achDebits',
      permissionLevel: 'write',
      bankAccount: actionBankAccount,
    })
  ) {
    missingPermissions.push('ACH debits');
  }

  if (missingPermissions.length === 0) {
    return undefined;
  }

  return `You do not have permission to create ${missingPermissions.join(' or ')}.`;
};
